import { OptionType } from '@evinced-private/ui-common';

import { NO_OPTION_VALUE_SUFFIX } from 'src/common/consts/FilterNoOptionConstants';
import { WARNING_EMOJI } from 'src/common/consts/platformConfigConsts';
import { ConfigurationLabel, ConfigurationLabelValue } from 'src/common/types/ConfigurationTypes';

import { parseLabel } from '../../components/shared/helpers';
import { FILTER_TYPES, FilterType } from '../../types/filter-section-v2/FilterConstants';

const LABEL_SEPARATOR = ':';

const normalize = (value: string): string => value.trim().toLowerCase();

const isNoOptionSelection = (optionValue: OptionType | string | number): boolean => {
	if (typeof optionValue !== 'string') {
		return false;
	}

	const colonIndex = optionValue.indexOf(LABEL_SEPARATOR);
	if (colonIndex <= 0) {
		return false;
	}

	const valuePart = optionValue.substring(colonIndex + 1).trim();
	return normalize(valuePart) === normalize(NO_OPTION_VALUE_SUFFIX);
};

export const extractValueFromLabel = (label: string): string => {
	const parsed = parseLabel(label);
	return parsed?.value ?? label;
};

export const getConfiguredValuesFromCoreLabel = (
	label: ConfigurationLabel | undefined
): Set<string> => {
	if (!label?.values) {
		return new Set<string>();
	}

	return new Set(
		label.values.map((v) => (typeof v === 'string' ? v : (v as ConfigurationLabelValue).value))
	);
};

export const markOptionsWithWarnings = (
	options: OptionType[],
	configuredValuesSet: Set<string>,
	filterType: FilterType,
	displayName: string
): OptionType[] => {
	return options.map((option) => {
		// Never warn on the special "No option" selection (e.g. "Environment: __ev_no_option__")
		if (isNoOptionSelection(option.value)) {
			return option;
		}

		// Use the label as it contains the extracted value (already processed by transformLabelsToV2Format)
		// The label is what the user sees and should match the configured values
		const optionLabel = String(option.label);

		// Extract the value part if the label contains a colon (e.g., "Environment: development" -> "development")
		// This handles cases where labels come from extractLabelOptionsFromTestRows which includes the full label
		const colonIndex = optionLabel.indexOf(LABEL_SEPARATOR);
		const valuePart = colonIndex > 0 ? optionLabel.substring(colonIndex + 1).trim() : optionLabel;

		// Check if the value part matches any configured value (case-insensitive)
		const matchesConfiguration = Array.from(configuredValuesSet).some(
			(configuredValue) => configuredValue.toLowerCase() === valuePart.toLowerCase()
		);

		// Only add warning if:
		// 1. The option doesn't match any configured value
		// 2. There are configured values to compare against (to avoid false positives when config is empty)
		if (!matchesConfiguration && configuredValuesSet.size > 0) {
			// Generate tooltip content using the display name from configuration
			// Use only the value part to avoid duplication (e.g., "Environment: Environment: development")
			const tooltipContent = `${displayName}: ${valuePart}\n${WARNING_EMOJI} Unknown value`;

			return {
				...option,
				label: `${WARNING_EMOJI} ${option.label}`,
				// Add custom tooltip with dynamic key and label
				customTooltip: tooltipContent
			};
		}

		return option;
	});
};

export const markOptionsWithWarningsFromKeyValue = (
	options: OptionType[],
	configuredValues: {
		environment: Set<string>;
		unitId: Set<string>;
		environmentDisplayName: string;
		unitIdDisplayName: string;
	},
	extractLabelKey: (value: string) => string | null
): OptionType[] => {
	return options.map((option) => {
		// Never warn on the special "No option" selection (e.g. "Environment: __ev_no_option__")
		if (isNoOptionSelection(option.value)) {
			return option;
		}

		const labelString = option.value as string;
		const labelKey = extractLabelKey(labelString);
		const parsed = parseLabel(labelString);
		const labelValue = parsed?.value ?? '';

		// Determine which configured values set to use and whether to warn
		let shouldWarn = false;
		let displayName: string | null = null;
		let configuredValuesSet: Set<string> = new Set();

		if (labelKey === FILTER_TYPES.ENVIRONMENT.toLowerCase()) {
			displayName = configuredValues.environmentDisplayName;
			configuredValuesSet = configuredValues.environment;
			shouldWarn =
				configuredValuesSet.size > 0 &&
				!Array.from(configuredValuesSet).some(
					(configuredValue) => configuredValue.toLowerCase() === labelValue.toLowerCase()
				);
		} else if (labelKey === FILTER_TYPES.UNIT_ID.toLowerCase()) {
			displayName = configuredValues.unitIdDisplayName;
			configuredValuesSet = configuredValues.unitId;
			shouldWarn =
				configuredValuesSet.size > 0 &&
				!Array.from(configuredValuesSet).some(
					(configuredValue) => configuredValue.toLowerCase() === labelValue.toLowerCase()
				);
		}

		if (shouldWarn && displayName) {
			const tooltipContent = `${displayName}: ${labelValue}\n${WARNING_EMOJI} Unknown value`;

			return {
				...option,
				label: `${WARNING_EMOJI} ${option.label}`,
				customTooltip: tooltipContent
			};
		}

		return option;
	});
};
